import React, { useState, useEffect }from "react";
import {toast} from "react-toastify";
import ProfileSectionCard from "./ProfileSectionCard";
import UserService from "services/user.service";
import AuthService from "services/auth.service";
import { CheckCircleFilled, EyeOutlined, EyeInvisibleOutlined} from "@ant-design/icons";
import { ForgotPasswordModal } from "components/ForgotPassword/ForgotPasswordModal";

const ChangePassword = ({ isReadOnly = false }) => {
  // Show/hide password states
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  });

  const pswd = formData.new_password;
  const isLengthValid = pswd.length >= 8;
  const hasLower = /[a-z]/.test(pswd);
  const hasUpper = /[A-Z]/.test(pswd);
  const hasNumber = /[0-9]/.test(pswd);

  const isNewPasswordValid = isLengthValid && hasLower && hasUpper && hasNumber;

  const resetForm = () => {
    setFormData({
      current_password: '',
      new_password: '',
      new_password_confirmation: ''
    });
    setFieldErrors({});
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
  };

  const handleChange = (e) => {
    setFormData({...formData, [e.target.name]: e.target.value});
     // Clear field-specific error as user types
    if (fieldErrors[e.target.name]) {
      setFieldErrors({ ...fieldErrors, [e.target.name]: null });
    }
  };

  const handleSave = async () => {
    setFieldErrors({});
    let errors = {};

    // Check for empty fields
   if (!formData.current_password) {
      errors.current_password = "Required";
    }

    if (!formData.new_password) {
      errors.new_password = "Required";
    } else if (!isNewPasswordValid) {
      errors.new_password = "Password does not meet the requirements.";
    }

    if (!formData.new_password_confirmation) {
      errors.new_password_confirmation = "Required";
    } else if (formData.new_password !== formData.new_password_confirmation) {
      errors.new_password_confirmation = "The passwords do not match. Please try again.";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      throw new Error("Validation failed");
    }

    const payload = {
      current_password: formData.current_password,
      password: formData.new_password,
      password_confirmation: formData.new_password_confirmation
    };

    try {
      await UserService.changePassword(payload);
      toast.success("Password changed! All sessions will be signed out and required to login again.");
      AuthService.logout();
      setTimeout(() => {
        window.location.replace("/eb/login");
      }, 2000);
    } catch (error) {
      const responseData = error.response?.data;

      if (error.response?.status === 401 && !responseData?.errors && !responseData?.error) {
        AuthService.logout();
        window.location.replace("/eb/login");
      } else {
        // Extract backend error message for current_password
        const errorMessage = 
          (Array.isArray(responseData?.errors) ? responseData.errors.join(', ') : responseData?.error) || 
          "The current password you entered is incorrect. Please try again.";

        setFieldErrors({ current_password: errorMessage });
      }

      throw error;
    }
  };

  const EyeIcon = ({ show, toggle }) => (
    <span
      onClick={toggle}
      className="clickable"
      style={{
        fontSize: "18px",
        position: "absolute",
        right: "15px",
        top: "50%",
        transform: "translateY(-50%)",
        cursor: "pointer",
        color: "#4E4E4E"
      }}
    >
      {show ? <EyeOutlined/> : <EyeInvisibleOutlined/>}
    </span>
  );

  const CheckItem = ({ isValid, label }) => (
    <div className={`d-flex align-items-center mb-2 ${isValid ? "text-success" : "text-muted"}`}>
      <CheckCircleFilled
        style={{
          marginRight: "8px",
          fontSize: "20px",
          color: isValid ? "#78D1B7" : "#C7C7CC",
          transition: "color 0.3s ease"
        }}
      />
      <span className="text-muted">{label}</span>
    </div>
  );

  return (
    <ProfileSectionCard
      title="Password"
      showEditButton={!isReadOnly}
      onSave={handleSave}
      onCancel={resetForm}
      >
        {({ isEditing }) => (
          <div className="profile-info-group">
            {/* READ-ONLY MODE */}
            {!isEditing && (
              <div className="profile-info-group">
                <div className= "flex-grow-1 profile-info-row mt-4">
                  <p className="profile-info-label mb-0">Password</p>
                  <div className="profile-info-value">••••••••••••••••</div>
                </div>
              </div>
            )}

            {/* EDIT MODE */}
            {isEditing && (
              <div className="profile-info-group">
                <div className= "flex-grow-1 profile-info-row mt-4">
                  <label className="profile-info-label">Current Password<span className="text-danger fWeight-500">*</span></label>
                  <div className="profile-info-value" style={{ display: 'block' }}>
                    <div className="position-relative w-100">
                      <input
                        type={showCurrent ? "text" : "password"}
                        name="current_password"
                        placeholder="Enter current password"
                        value={formData.current_password}
                        onChange={handleChange}
                        className={`form-control py-6 px-4 profile-info-value pr-5 ${fieldErrors.current_password ? 'border-danger' : ''}`}
                      />
                      <EyeIcon show={showCurrent} toggle={() => setShowCurrent(!showCurrent)} />
                    </div>
                    <div className="d-flex justify-content-between mt-2" style={{ fontSize: "12px", minHeight: "18px" }}>
                      <div className="text-danger mt-2" style={{ fontSize: "12px" }}>
                        {fieldErrors.current_password && fieldErrors.current_password}
                      </div>
                      <ForgotPasswordModal/>
                    </div>
                  </div>
                </div>
                <div className= "flex-grow-1 profile-info-row mt-4">
                  <label className="profile-info-label">New Password<span className="text-danger fWeight-500">*</span></label>
                  <div className="profile-info-value" style={{ display: 'block' }}>
                    <div className="position-relative w-100">
                      <input
                        type={showNew ? "text" : "password"}
                        name="new_password"
                        placeholder="Enter new password"
                        value={formData.new_password}
                        onChange={handleChange}
                        className={`form-control py-6 px-4 profile-info-value pr-5 ${fieldErrors.new_password ? 'border-danger' : ''}`}
                      />
                      <EyeIcon show={showNew} toggle={() => setShowNew(!showNew)} />
                    </div>
                     {fieldErrors.new_password && (
                        <div className="text-danger mt-2" style={{ fontSize: "12px" }}>
                          {fieldErrors.new_password}
                        </div>
                      )}

                    <div className="mt-3" style={{ fontSize: "12px" }}>
                      <div className="mb-4 text-muted fweight-500">Password must contain:</div>
                      <CheckItem isValid={isLengthValid} label="At least 8 characters long" />
                      <CheckItem isValid={hasLower} label="Minimum one lowercase" />
                      <CheckItem isValid={hasUpper} label="Minimum one uppercase" />
                      <CheckItem isValid={hasNumber} label="Minimum one number" />
                    </div>
                  </div>
                </div>
                <div className= "flex-grow-1 profile-info-row mt-4">
                  <label className="profile-info-label">Verify New Password<span className="text-danger fWeight-500">*</span></label>
                  <div className="profile-info-value" style={{ display: 'block' }}>
                    <div className="position-relative">
                      <input
                        type={showConfirm ? "text" : "password"}
                        name="new_password_confirmation"
                        placeholder="Re-enter new password"
                        value={formData.new_password_confirmation}
                        onChange={handleChange}
                        className={`form-control py-6 px-4 profile-info-value pr-5 ${fieldErrors.new_password_confirmation ? 'border-danger' : ''}`}
                      />
                      <EyeIcon show={showConfirm} toggle={() => setShowConfirm(!showConfirm)} />
                    </div>
                    {fieldErrors.new_password_confirmation && (
                      <div className="text-danger mt-2" style={{ fontSize: "12px" }}>
                        {fieldErrors.new_password_confirmation}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
       )}
    </ProfileSectionCard>
	);
};

export default ChangePassword;